import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import Header from "../components/Header";
import { getPermissions } from "../api/configHelper";

import {Search,Folder,Settings,Users,BarChart3,Database,FileText,Grid3x3,List,ExternalLink,FileText as FileTextIcon, FileSpreadsheet, FileInput, LayoutDashboard} from "lucide-react";

const menuKey = (value) => (value || "").trim().toLowerCase();

const buildReactWebPathUrl = (reactWebPath, params = {}) => {
  const cleanPath = reactWebPath.trim();
  const separator = cleanPath.includes("?") ? "&" : "?";
  const queryParams = new URLSearchParams();

  Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null)
    .forEach(([key, value]) => queryParams.append(key, value));

  return `${cleanPath}${separator}${queryParams.toString()}`;
};

const navigateToDirectSubproject = (mdlId, primeColumn, primeId, searchKeyword, reactWebPath) => {
  if (!reactWebPath) return false;

  window.location.href = buildReactWebPathUrl(reactWebPath, {
    mdlId,
    primecolumn: primeColumn || "",
    primeid: primeId,
    searchKeyword: searchKeyword || "",
  });
  return true;
};

function HeaderActionButtons({ onSearch, onReports, onDashboard, permissions }) {
  const actions = [
    { label: "Search (F8)", title: "Search (F8)", Icon: Search, onClick: onSearch },
    { label: "Adv Reports (F9)", title: "Adv Reports (F9)", Icon: FileText, onClick: onReports, permission: "advReports" },
    { label: "Dashboard", title: "Dashboard", Icon: LayoutDashboard, onClick: onDashboard, permission: "dashboard" },
  ].filter((action) => !action.permission || permissions?.[action.permission] === 1);

  return (
    <div className="header-actions">
      {actions.map(({ label, title, Icon, onClick }) => (
        <div key={label} className="header-action">
          <button
            onClick={onClick}
            className="top-icon-button"
            title={title}
            aria-label={title}
          >
            <Icon size={13} />
          </button>
          <span className="top-icon-label">
            {label}
          </span>
        </div>
      ))}
    </div>
  );
}

function useMenuData() {
  const [menus, setMenus] = useState([]);
  const [allSubMenus, setAllSubMenus] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const id = sessionStorage.getItem("userId");
    if (!id) return navigate("/");
    loadMenus(id);
  }, []);

  const loadMenus = async (uid) => {
    try {
      const result = await api.GetMenuTypes(uid);
      const list = result?.Table || [];
      setMenus(list);

      const resp = await api.GetAllSubMenuList(uid);
      
      // Store the full API response in session storage
      if (resp) {
        sessionStorage.setItem('allSubMenuList', JSON.stringify(resp));
      }
      
      const allSubs = resp?.Table || [];
      const subMenusMap = {};
      allSubs.forEach(sub => {
        const type = menuKey(sub.MdlType);
        if (!subMenusMap[type]) subMenusMap[type] = [];
        subMenusMap[type].push(sub);
      });
      setAllSubMenus(subMenusMap);

      const mdlDirect = sessionStorage.getItem("mdldirect");
      if (mdlDirect && sessionStorage.getItem("mdldirectHandled") !== mdlDirect) {
        const [, seqId, primeId] = mdlDirect.split("!");
        if (seqId?.trim() && primeId?.trim()) {
          try {
            const moduleResult = await api.GetMdlIdBySeqId(seqId.trim());
            const mdlId = moduleResult?.mdlId;
            const moduleMenu = allSubs.find(
              (sub) => menuKey(sub.MdlId) === menuKey(mdlId)
            );

            if (mdlId && moduleMenu?.ReactWebPath) {
              sessionStorage.setItem("mdldirectHandled", mdlDirect);
              navigateToDirectSubproject(
                mdlId,
                moduleResult?.primeColumn || moduleMenu.PrimeColumn,
                primeId.trim(),
                "",
                moduleMenu.ReactWebPath
              );
            }
          } catch (err) {
            console.error("Failed to open direct module:", err);
          }
        }
      }
    } catch {
      setMenus([]);
      setAllSubMenus({});
    }
  };

  return { menus, allSubMenus };
}

function useConfigPermissions() {
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const perms = await getPermissions();
        setPermissions(perms);
      } catch (err) {
        console.error("Failed to load permissions:", err);
        setPermissions({});
      }
    };
    loadPermissions();
  }, []);

  return permissions;
}

function SearchEverythingModal({
  globalSearch,
  setGlobalSearch,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  handleGlobalSearch,
  searchLoading,
  searchError,
  searchResults,
  setSearchResults,
  modulesList,
  selectedModules,
  setSelectedModules,
  getDefaultFromDate,
  getDefaultToDate,
  onClose,
}) {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('card');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const searchInputRef = useRef(null);
  const closeButtonRef = useRef(null);
  const firstResultRef = useRef(null);
  const newwinRef = useRef(null);
  
  // Shared function to navigate to subproject
  const navigateToSubproject = (mdlId, primeColumn, primeId, searchKeyword, reactWebPath) => {
    if (!reactWebPath) return;
    
    const targetUrl = buildReactWebPathUrl(reactWebPath, {
      mdlId,
      primecolumn: primeColumn || "",
      primeid: primeId || "",
      searchKeyword: searchKeyword || "",
    });
    
    if (window.confirm('Open in new window?')) {
      const win = window.open(targetUrl, '_blank');
      if (win) {
        newwinRef.current = win;
        win.name = 'newwin';
      }
    } else {
      if (newwinRef.current && !newwinRef.current.closed) {
        newwinRef.current.location.href = targetUrl;
      }
    }
  };
  
  // Focus search input when modal opens
  useEffect(() => {
    setTimeout(() => {
      if (searchInputRef.current && !showCloseConfirm) {
        searchInputRef.current.focus();
      } else if (closeButtonRef.current && showCloseConfirm) {
        closeButtonRef.current.focus();
      }
    }, 100);
  }, [showCloseConfirm]);
  
  // ESC key handler to show close confirmation
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (showCloseConfirm) {
          setShowCloseConfirm(false);
        } else {
          setShowCloseConfirm(true);
        }
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showCloseConfirm]);
  
  // Prepare parsed search-result items (flatten SearchResult JSON from each log row)
  let parsedItems = [];
  try {
    const rows = searchResults?.logData?.Table || [];
    parsedItems = rows.flatMap((r) => {
      if (!r?.SearchResult) return [];
      try {
        const parsed = JSON.parse(r.SearchResult);
        if (Array.isArray(parsed)) {
          const moduleName = r.ModuleName || r.MdlName || r.MdlDesc || r.MdlId || r.Mdlid || '';
          const actionTime = r.ActionTime || r.actionTime || r.Actiontime || '';
          const username = r.Username || r.UserName || r.User || '';
          return parsed.map(item => ({ 
            ...item, 
            ModuleName: moduleName, 
            actionTime: actionTime, 
            Username: username, 
            _MdlId: r.Mdlid || r.MdlId, 
            _PrimeId: r.PrimeId,
            _ReactWebPath: r.ReactWebPath || r.ReactwebPath,
            _PrimeColumn: r.PrimeColumn
          }));
        }
        return [];
      } catch (e) {
        return [];
      }
    });
  } catch (e) {
    parsedItems = [];
  }
  const parsedCols = parsedItems.length > 0 ? Object.keys(parsedItems[0]).filter(k => !k.startsWith('_')) : [];

  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}></div>

      <div className="fixed inset-0 z-50 flex items-center justify-center p-2">
        <div onClick={(e) => e.stopPropagation()} className="w-full max-w-[95vw] h-[98vh] overflow-hidden flex flex-col bg-white rounded-xl shadow-2xl">
          {/* Small Close Button */}
          <div className="flex justify-end px-2 py-1 border-b border-slate-200 bg-slate-50">
            <button 
              onClick={onClose} 
              className="p-1 text-red-500 transition-colors rounded hover:text-red-700 hover:bg-red-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Search Filters */}
          <div className="p-3 bg-white border-b border-slate-200">
            <div className="flex flex-wrap items-end gap-3">
              {/* Keyword Input */}
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-slate-400" />
                  <input
                    ref={searchInputRef}
                    value={globalSearch}
                    onChange={(e) => setGlobalSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleGlobalSearch();
                      } else if (e.key === 'ArrowDown' && parsedItems.length > 0) {
                        e.preventDefault();
                        firstResultRef.current?.focus();
                      }
                    }}
                    placeholder="Search across all modules and logs..."
                    className="w-full pl-10 pr-4 py-1.5 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                  />
                </div>
              </div>
              
              {/* Date Fields - inline with labels */}
              <div className="flex items-end gap-2">
                <div>
                  <label className="block mb-1 text-xs font-medium text-slate-600">From</label>
                  <input 
                    type="date" 
                    value={fromDate} 
                    onChange={(e)=>setFromDate(e.target.value)} 
                    className="px-2 py-1 text-xs transition-all bg-white border rounded-lg cursor-pointer border-slate-300 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-xs font-medium text-slate-600">To</label>
                  <input 
                    type="date" 
                    value={toDate} 
                    onChange={(e)=>setToDate(e.target.value)} 
                    className="px-2 py-1 text-xs transition-all bg-white border rounded-lg cursor-pointer border-slate-300 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary"
                  />
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center gap-2">
              
                <button 
                  onClick={() => handleGlobalSearch()} 
                  disabled={searchLoading} 
                  className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-white transition-colors bg-blue-600 rounded-lg shadow-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {searchLoading ? (
                    <>
                      <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="w-3 h-3" />
                      Results
                    </>
                  )}
                </button>
                  <button
                  onClick={() => { setFromDate(getDefaultFromDate()); setToDate(getDefaultToDate()); setSelectedModules([]); setGlobalSearch(""); setSearchResults(null); searchInputRef.current?.focus(); }}
                  className="px-3 py-1.5 text-xs font-medium transition-colors border rounded-lg text-slate-600 bg-slate-100 border-slate-200 hover:bg-slate-200"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {searchError && (
            <div className="px-4 py-2 mx-4 text-xs text-red-600 border border-red-100 rounded-lg bg-red-50">
              {searchError}
            </div>
          )}

          {/* Results Section */}
          <div className="flex-1 px-4 py-2 overflow-auto">
            {parsedItems && parsedItems.length > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between pb-2 border-b border-slate-200">
                  <div>
                    <p className="text-xs font-medium text-slate-500">{searchResults?.logData?.Table?.length || parsedItems.length} Results</p>
                  </div>
                  <div className="flex items-center gap-1 p-0.5 rounded bg-slate-100">
                    <button
                      onClick={() => setViewMode('card')}
                      className={`p-1 rounded transition-colors ${
                        viewMode === 'card'
                          ? 'bg-white text-primary shadow-sm'
                          : 'text-slate-600 hover:text-slate-800'
                      }`}
                      title="Card View"
                    >
                      <Grid3x3 className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => setViewMode('table')}
                      className={`p-1 rounded transition-colors ${
                        viewMode === 'table'
                          ? 'bg-white text-primary shadow-sm'
                          : 'text-slate-600 hover:text-slate-800'
                      }`}
                      title="Table View"
                    >
                      <List className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {/* CARD VIEW */}
                {viewMode === 'card' && (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 overflow-auto max-h-[calc(98vh-150px)]">
                    {parsedItems.map((row, ridx) => {
                      const displayKeys = Object.keys(row).filter(k => !k.startsWith('_') && k !== 'ModuleName' && k !== 'actionTime'&& k !== 'Username');
                      const nonEmptyKeys = displayKeys.filter(k => row[k] !== null && row[k] !== undefined && String(row[k]).trim() !== '');
                      
                      const handleModuleClick = () => {
                        console.log('Module clicked:', row);
                        const mdlId = row._MdlId;
                        const primeId = row._PrimeId;
                        const primeColumn = row._PrimeColumn;
                        const reactWebPath = row._ReactWebPath;
                        const searchKeyword = globalSearch || '';
                        
                        console.log('Navigating to subproject with query params:', { mdlId, primeColumn, primeId, searchKeyword });
                        
                        navigateToSubproject(mdlId, primeColumn, primeId, searchKeyword, reactWebPath);
                      };
                      
                      return (
                        <div 
                          key={ridx} 
                          ref={ridx === 0 ? firstResultRef : null}
                          tabIndex={0}
                          className="p-3 bg-white border rounded-lg shadow-sm border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/50"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <button 
                                onClick={handleModuleClick}
                                className="flex items-center gap-1 text-xs font-medium tracking-wide text-blue-600 uppercase cursor-pointer hover:text-blue-800 hover:underline"
                              >
                                <ExternalLink className="w-3 h-3" />
                                {row.ModuleName || row._MdlId || '-'}
                              </button>
                              <p className="text-xs text-slate-600 mt-0.5">{row.Username || '-'}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-slate-600">{row.actionTime ? new Date(row.actionTime).toLocaleString() : '-'}</p>
                            </div>
                          </div>

                          <div className="mt-2 text-xs text-slate-600">
                            {nonEmptyKeys.length === 0 ? (
                              <div className="text-slate-400">No details</div>
                            ) : (
                              <ul className="space-y-0.5">
                                {nonEmptyKeys.map((k) => (
                                  <li key={k} className="flex items-start gap-2">
                                    <span className="w-24 font-medium truncate text-slate-700">{k}:</span>
                                    <span className="break-words truncate text-slate-600">{String(row[k])}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* TABLE VIEW */}
                {viewMode === 'table' && (
                  <div className="overflow-auto max-h-[calc(98vh-150px)] border border-slate-200 rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 border-b bg-slate-100 border-slate-200">
                        <tr>
                          <th className="px-3 py-2 font-semibold text-left text-slate-700">Module</th>
                          <th className="px-3 py-2 font-semibold text-left text-slate-700">User</th>
                          <th className="px-3 py-2 font-semibold text-left text-slate-700">Time</th>
                          <th className="px-3 py-2 font-semibold text-left text-slate-700">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {parsedItems.map((row, ridx) => {
                          const displayKeys = Object.keys(row).filter(k => !k.startsWith('_') && k !== 'ModuleName' && k !== 'actionTime' && k !== 'Username');
                          const nonEmptyKeys = displayKeys.filter(k => row[k] !== null && row[k] !== undefined && String(row[k]).trim() !== '');
                          const detailsText = nonEmptyKeys.map(k => `${k}: ${String(row[k]).substring(0, 30)}`).join(' | ');
                          
                          const handleModuleClick = () => {
                            console.log('Module clicked (table):', row);
                            const mdlId = row._MdlId;
                            const primeId = row._PrimeId;
                            const primeColumn = row._PrimeColumn;
                            const reactWebPath = row._ReactWebPath;
                            const searchKeyword = globalSearch || '';
                            
                            console.log('Navigating to subproject with query params:', { mdlId, primeColumn, primeId, searchKeyword });
                            
                            navigateToSubproject(mdlId, primeColumn, primeId, searchKeyword, reactWebPath);
                          };
                          
                          return (
                            <tr 
                              key={ridx} 
                              ref={ridx === 0 ? firstResultRef : null}
                              tabIndex={0}
                              className="transition-colors cursor-pointer hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-primary/50"
                            >
                              <td className="px-3 py-2">
                                <button 
                                  onClick={handleModuleClick}
                                  className="flex items-center gap-1 font-medium text-blue-600 cursor-pointer hover:text-blue-800 hover:underline"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  {row.ModuleName || row._MdlId || '-'}
                                </button>
                              </td>
                              <td className="px-3 py-2 text-slate-600">{row.Username || '-'}</td>
                              <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{row.actionTime ? new Date(row.actionTime).toLocaleString() : '-'}</td>
                              <td className="px-3 py-2 text-slate-600 break-words min-w-[200px]" title={detailsText}>{detailsText || 'N/A'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <Search className="w-12 h-12 mb-3 opacity-20" />
                <p className="text-sm">No results found</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Close Confirmation Dialog */}
      {showCloseConfirm && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setShowCloseConfirm(false);
              onClose();
            }
          }}
        >
          <div className="max-w-sm p-6 mx-4 bg-white rounded-lg shadow-xl">
            <h3 className="mb-2 text-lg font-semibold text-slate-800">Close Search?</h3>
            <p className="mb-4 text-sm text-slate-600">Are you sure you want to close the Search modal?</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowCloseConfirm(false)}
                className="px-4 py-2 text-sm font-medium rounded-lg text-slate-600 bg-slate-100 hover:bg-slate-200"
              >
                Cancel
              </button>
              <button
                ref={closeButtonRef}
                onClick={() => {
                  setShowCloseConfirm(false);
                  onClose();
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ============================================================
   A) MODAL VARIANT
============================================================ */
export function MenuModalVariant() {
  const { menus, allSubMenus } = useMenuData();
  const permissions = useConfigPermissions();
  const uid = localStorage.getItem("userId") || sessionStorage.getItem("userId") ;
  const navigate = useNavigate();

  // Map Mnu_Group to corresponding icon
  const getMenuGroupIcon = (menuGroup) => {
    switch (menuGroup?.trim()) {
      case "Entry Form":
        return FileInput;
      case "Report Form":
        return FileTextIcon;
      case "Misc Form":
        return FileSpreadsheet;
      default:
        return FileTextIcon; // Default icon
    }
  };

  // Helper function to get default date range (last 30 days)
  const getDefaultFromDate = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    return thirtyDaysAgo.toISOString().split('T')[0];
  };

  const getDefaultToDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const [activeMenu, setActiveMenu] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [subList, setSubList] = useState([]);
  const [search, setSearch] = useState("");
  const [mainSearch, setMainSearch] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getDefaultToDate());
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [modulesList, setModulesList] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const modalRef = useRef(null);
  const searchRef = useRef(null);
  const subSearchRef = useRef(null);
  const searchModalRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") closeModal();
      if (e.key === "F8") {
        e.preventDefault();
        setShowSearchModal(true);
      }
      if (e.key === "F9") {
        e.preventDefault();
        navigate("/advance-report");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate]);

  useEffect(() => {
    setTimeout(() => {
      if (searchRef.current) {
        searchRef.current.focus();
      }
    }, 100);
  }, []);

  useEffect(() => {
    const loadModules = async () => {
      try {
        const uidLocal = sessionStorage.getItem("userId") || localStorage.getItem("userId");
        const resp = await api.GetAllSubMenuList(uidLocal);
        const list = resp?.Table || [];
        setModulesList(list);
      } catch (err) {
        setModulesList([]);
      }
    };
    loadModules();
  }, []);

  const handleGlobalSearch = async () => {
    setSearchError("");
    setSearchLoading(true);
    
    // Validate that dates are provided
    if (!fromDate || !toDate) {
      setSearchError("Date range is mandatory");
      setSearchLoading(false);
      return;
    }

    try {
      const user = sessionStorage.getItem("userName") || localStorage.getItem("userName") || "";
      const useDateFilter = 1; // Dates are always mandatory
      const mdlIds = selectedModules.length > 0 ? (selectedModules || []).join(",") : null;
      const resp = await api.GetSearchAndLog(globalSearch || "", user, useDateFilter, mdlIds, fromDate, toDate);

      // For each log row, fetch module display values
      const rows = resp?.logData?.Table || [];
      const moduleDetails = await Promise.all(rows.map(async (r) => {
        try {
          const md = await api.GetModuleDisplayValues(r.MdlId, r.PrimeId);
          return { log: r, moduleData: md?.data || md };
        } catch (e) {
          return { log: r, error: e?.message || String(e) };
        }
      }));

      setSearchResults({ ...resp, moduleDetails });
      setShowSearchModal(true);
    } catch (err) {
      setSearchError(err?.message || "Failed to fetch");
      setSearchResults(null);
    } finally {
      setSearchLoading(false);
    }
  };

  const openModalFor = async (idx, menu) => {
    setActiveMenu(idx);
    setSearch("");
    setModalOpen(true);

    try {
      const resp = await api.GetSubMenuList(uid, menu.ModuleType);
      setSubList(resp.Table || []);
    } catch {
      setSubList([]);
    }

    setTimeout(() => {
      subSearchRef.current?.focus();
    }, 60);
  };

  const closeModal = () => {
    setModalOpen(false);
    setActiveMenu(null);
  };

  const filteredItems = [];
  const filteredMenus = [];
  if (mainSearch.trim()) {
    const q = mainSearch.toLowerCase();
    menus.forEach((m, idx) => {
      const menuMatches = (m.MdlDisplayName || m.ModuleType)?.toLowerCase().includes(q);
      if (menuMatches) {
        const menuItem = { type: 'menu', data: { ...m, originalIndex: idx } };
        filteredItems.push(menuItem);
        filteredMenus.push(menuItem.data);
      }
      const matchingSubs = allSubMenus[menuKey(m.ModuleType)]?.filter((s) =>
        s.MdlDisplayName?.toLowerCase().includes(q)
      ) || [];
      matchingSubs.forEach((s) => {
        filteredItems.push({ type: 'submenu', data: { ...s, parentMenu: m, parentIndex: idx } });
      });
    });
  } else {
    const menuItems = menus.map((m, idx) => ({ type: 'menu', data: { ...m, originalIndex: idx } }));
    filteredItems.push(...menuItems);
    filteredMenus.push(...menuItems.map(item => item.data));
  }

  const filteredSubs = subList.filter((s) =>
    s.MdlDisplayName?.toLowerCase().includes(search.toLowerCase())
  );

  const icons = [Folder, Settings, Users, BarChart3, Database, FileText];

  return (
    <div className="min-h-screen">
      <Header
        headerActions={
          <HeaderActionButtons
            onSearch={() => setShowSearchModal(true)}
            onReports={() => navigate("/advance-report")}
            onDashboard={() => navigate("/dashboard")}
            permissions={permissions}
          />
        }
      />
      
      
      {/* MAIN MENU */}
      <div className="max-w-screen-xl p-5 pt-16 mx-auto sm:pt-24">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold text-primary">
            Modules
          </h2>
      </div>

      {/* SEARCH MODAL */}
      {showSearchModal && (
        <SearchEverythingModal
          globalSearch={globalSearch}
          setGlobalSearch={setGlobalSearch}
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          handleGlobalSearch={handleGlobalSearch}
          searchLoading={searchLoading}
          searchError={searchError}
          searchResults={searchResults}
          setSearchResults={setSearchResults}
          modulesList={modulesList}
          selectedModules={selectedModules}
          setSelectedModules={setSelectedModules}
          getDefaultFromDate={getDefaultFromDate}
          getDefaultToDate={getDefaultToDate}
          onClose={() => setShowSearchModal(false)}
        />
      )}

        <div className="relative mb-2 bg-[#f1f1f1] p-1 rounded-lg">
          <input
            ref={searchRef}
            value={mainSearch}
            onChange={(e) => setMainSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                const firstItem = document.querySelector('.menu-item');
                if (firstItem) firstItem.focus();
              }
            }}
            placeholder="Search main menus..."
            className="w-full h-10 pl-10 text-sm bg-white input input-bordered"
          />
          <Search className="absolute w-6 h-6 -translate-y-1/2 left-3 top-1/2 text-base-content/70" />
        </div>

        {/* LIST VIEW WHEN SEARCHING */}
        {mainSearch.trim() !== "" ? (
          <div className="space-y-2 min-h-[70vh]">
            {filteredItems.length === 0 && (
              <p className="text-sm text-base-content/60">No menus found</p>
            )}

            {filteredItems.map((item, i) => {
              if (item.type === 'menu') {
                const m = item.data;
                return (
                  <div
                    key={`menu-${m.originalIndex}`}
                    onClick={() => openModalFor(m.originalIndex, m)}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        const next = e.target.nextElementSibling;
                        if (next && next.classList.contains('menu-item')) next.focus();
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        const prev = e.target.previousElementSibling;
                        if (prev && prev.classList.contains('menu-item')) prev.focus();
                      } else if (e.key === 'Enter') {
                        e.target.click();
                      }
                    }}
                    className="p-3 rounded-lg cursor-pointer border border-[rgba(var(--base-content-rgb),0.1)]
                               hover:bg-[rgba(var(--base-200-rgb),0.2)] transition menu-item"
                    tabIndex="0"
                  >
                    <div className="text-lg font-semibold text-base-content">
                      {m.MdlDisplayName || m.ModuleType}
                    </div>
                    <div className="text-xs text-base-content/60">
                      {allSubMenus[menuKey(m.ModuleType)]?.length || 0} Modules
                    </div>
                  </div>
                );
               } else {
                 const s = item.data;
                 const Icon = getMenuGroupIcon(s.Mnu_Group);
                 return (
                   <div
                     key={`submenu-${i}`}
                     onClick={() => {
                       if (s.ReactWebPath) {
                         const url = buildReactWebPathUrl(s.ReactWebPath, {
                           mdlId: s.MdlId,
                           TxnType: s.TxnType,
                           TxnSubType: s.TxnSubType,
                         });
                         window.location.href = url;
                       }
                     }}
                     onKeyDown={(e) => {
                       if (e.key === 'ArrowDown') {
                         e.preventDefault();
                         const next = e.target.nextElementSibling;
                         if (next && next.classList.contains('menu-item')) next.focus();
                       } else if (e.key === 'ArrowUp') {
                         e.preventDefault();
                         const prev = e.target.previousElementSibling;
                         if (prev && prev.classList.contains('menu-item')) prev.focus();
                       } else if (e.key === 'Enter') {
                         e.target.click();
                       }
                     }}
                     className="p-3 rounded-lg cursor-pointer border border-[rgba(var(--base-content-rgb),0.1)]
                                hover:bg-[rgba(var(--base-200-rgb),0.2)] transition menu-item"
                     tabIndex="0"
                   >
                     <div className="flex items-center gap-2 text-lg font-semibold text-base-content">
                       <Icon className="w-5 h-5" />
                       {s.MdlDisplayName}
                     </div>
                     <div className="text-xs text-base-content/60">
                       in {s.parentMenu.ModuleType}
                     </div>
                   </div>
                 );
               }
            })}
          </div>
        ) : (
          /* CARD VIEW */
          <div
            className="grid content-start gap-3"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
          >
            {filteredMenus.map((m) => {
              const Icon = icons[m.originalIndex % icons.length];
              return (
                <div
                  key={m.originalIndex}
                  onClick={() => openModalFor(m.originalIndex, m)}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      const next = e.target.nextElementSibling;
                      if (next && next.classList.contains('menu-item')) next.focus();
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      const prev = e.target.previousElementSibling;
                      if (prev && prev.classList.contains('menu-item')) prev.focus();
                    } else if (e.key === 'Enter') {
                      e.target.click();
                    }
                  }}
                  className="w-60 h-[72px] cursor-pointer rounded-lg p-3
                             bg-gradient-to-br from-[rgba(var(--primary-rgb),0.2)]
                             via-[rgba(var(--primary-focus-rgb),0.1)]
                             to-[rgba(var(--secondary-rgb),0.1)]
                             border border-[rgba(var(--base-content-rgb),0.1)]
                             backdrop-blur-xl shadow hover:scale-105 hover:shadow-xl
                             transition-all menu-item"
                  tabIndex="0"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold truncate text-primary">
                        {m.MdlDisplayName || m.ModuleType}
                      </h3>
                      <p className="text-xs text-primary/70"> </p>
                    </div>
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* MODAL */}
      {modalOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50"
            onClick={closeModal}
          ></div>

          <div
            ref={modalRef}
            onClick={closeModal}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-2xl p-6 border shadow-xl bg-white/20 backdrop-blur-xl border-white/30 rounded-2xl"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-base-content">
                    {menus[activeMenu]?.MdlDisplayName || menus[activeMenu]?.ModuleType}
                  </h3>
                  <p className="text-xs text-base-content/70">Select a module</p>
                </div>
                <button
                  onClick={closeModal}
                  className="text-lg font-bold text-base-content"
                >
                  ✕
                </button>
              </div>

              <input
                ref={subSearchRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    modalRef.current?.querySelector(".sub-menu-item")?.focus();
                  }
                }}
                placeholder="Search modules..."
                className="w-full mb-1 border input h-9 bg-white/40 backdrop-blur"
              />

              <div className="overflow-auto max-h-80">
                {filteredSubs.length === 0 && (
                  <p className="text-sm text-base-content/70">No modules found</p>
                )}

                {filteredSubs.map((s, i) => {
                  const Icon = getMenuGroupIcon(s.Mnu_Group);
                  return (
                    <div
                      key={i}
                      onClick={() => {
                        if (s.ReactWebPath) {
                          const url = buildReactWebPathUrl(s.ReactWebPath, {
                            mdlId: s.MdlId,
                            TxnType: s.TxnType,
                            TxnSubType: s.TxnSubType,
                          });
                          window.location.href = url;
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "ArrowDown") {
                          e.preventDefault();
                          const next = e.currentTarget.nextElementSibling;
                          if (next?.classList.contains("sub-menu-item")) next.focus();
                        } else if (e.key === "ArrowUp") {
                          e.preventDefault();
                          const prev = e.currentTarget.previousElementSibling;
                          if (prev?.classList.contains("sub-menu-item")) {
                            prev.focus();
                          } else {
                            subSearchRef.current?.focus();
                          }
                        } else if (e.key === "Enter") {
                          e.currentTarget.click();
                        }
                      }}
                      className="p-3 mb-1 transition rounded-lg cursor-pointer hover:bg-base-200/50 focus:outline-none focus:bg-base-200/50 sub-menu-item"
                      tabIndex="0"
                    >
                      <div className="flex items-center gap-2 text-lg font-bold">
                        <Icon className="w-5 h-5" />
                        {s.MdlDisplayName}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ============================================================
   B) SLIDE PANEL VARIANT
============================================================ */
export function MenuSlidePanelVariant() {
  const { menus, allSubMenus } = useMenuData();
  const permissions = useConfigPermissions();
  const uid = sessionStorage.getItem("userId");
  const navigate = useNavigate();

  // Map Mnu_Group to corresponding icon
  const getMenuGroupIcon = (menuGroup) => {
    switch (menuGroup?.trim()) {
      case "Entry Form":
        return FileInput;
      case "Report Form":
        return FileTextIcon;
      case "Misc Form":
        return FileSpreadsheet;
      default:
        return FileTextIcon; // Default icon
    }
  };

  // Helper function to get default date range (last 30 days)
  const getDefaultFromDate = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    return thirtyDaysAgo.toISOString().split('T')[0];
  };

  const getDefaultToDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const [activeIndex, setActiveIndex] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [subList, setSubList] = useState([]);
  const [search, setSearch] = useState("");
  const [mainSearch, setMainSearch] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getDefaultToDate());
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [modulesList, setModulesList] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const searchRef = useRef(null);
  const subSearchRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") closePanel();
      if (e.key === "F9") {
        e.preventDefault();
        navigate("/advance-report");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate]);

  useEffect(() => {
    setTimeout(() => {
      if (searchRef.current) {
        searchRef.current.focus();
      }
    }, 100);
  }, []);

  useEffect(() => {
    const loadModules = async () => {
      try {
        const uidLocal = sessionStorage.getItem("userId") || localStorage.getItem("userId");
        const resp = await api.GetAllSubMenuList(uidLocal);
        const list = resp?.Table || [];
        setModulesList(list);
      } catch (err) {
        setModulesList([]);
      }
    };
    loadModules();
  }, []);

  const handleGlobalSearch = async () => {
    setSearchError("");
    setSearchLoading(true);
    
    // Validate that dates are provided
    if (!fromDate || !toDate) {
      setSearchError("Date range is mandatory");
      setSearchLoading(false);
      return;
    }

    try {
      const user = sessionStorage.getItem("userName") || localStorage.getItem("userName") || "";
      const useDateFilter = 1; // Dates are always mandatory
      const mdlIds = selectedModules.length > 0 ? (selectedModules || []).join(",") : null;
      const resp = await api.GetSearchAndLog(globalSearch || "", user, useDateFilter, mdlIds, fromDate, toDate);

      // For each log row, fetch module display values
      const rows = resp?.logData?.Table || [];
      const moduleDetails = await Promise.all(rows.map(async (r) => {
        try {
          const md = await api.GetModuleDisplayValues(r.MdlId, r.PrimeId);
          return { log: r, moduleData: md?.data || md };
        } catch (e) {
          return { log: r, error: e?.message || String(e) };
        }
      }));

      setSearchResults({ ...resp, moduleDetails });
    } catch (err) {
      setSearchError(err?.message || "Failed to fetch");
      setSearchResults(null);
    } finally {
      setSearchLoading(false);
    }
  };

  const openPanelFor = async (idx, menu) => {
    setActiveIndex(idx);
    setSearch("");
    setPanelOpen(true);

    try {
      const resp = await api.GetSubMenuList(uid, menu.ModuleType);
      setSubList(resp.Table || []);
    } catch {
      setSubList([]);
    }

    setTimeout(() => {
      subSearchRef.current?.focus();
    }, 60);
  };

  const closePanel = () => {
    setPanelOpen(false);
    setActiveIndex(null);
  };

  const filteredItems = [];
  const filteredMenus = [];
  if (mainSearch.trim()) {
    const q = mainSearch.toLowerCase();
    menus.forEach((m, idx) => {
      const menuMatches = (m.MdlDisplayName || m.ModuleType)?.toLowerCase().includes(q);
      if (menuMatches) {
        const menuItem = { type: 'menu', data: { ...m, originalIndex: idx } };
        filteredItems.push(menuItem);
        filteredMenus.push(menuItem.data);
      }
      const matchingSubs = allSubMenus[menuKey(m.ModuleType)]?.filter((s) =>
        s.MdlDisplayName?.toLowerCase().includes(q)
      ) || [];
      matchingSubs.forEach((s) => {
        filteredItems.push({ type: 'submenu', data: { ...s, parentMenu: m, parentIndex: idx } });
      });
    });
  } else {
    const menuItems = menus.map((m, idx) => ({ type: 'menu', data: { ...m, originalIndex: idx } }));
    filteredItems.push(...menuItems);
    filteredMenus.push(...menuItems.map(item => item.data));
  }

  const filteredSubs = subList.filter((s) =>
    s.MdlDisplayName?.toLowerCase().includes(search.toLowerCase())
  );

  const icons = [Folder, Settings, Users, BarChart3, Database, FileText];

  return (
    <div className="min-h-screen">
      <Header
        headerActions={
          <HeaderActionButtons
            onSearch={() => setShowSearchModal(true)}
            onReports={() => navigate("/advance-report")}
            onDashboard={() => navigate("/dashboard")}
            permissions={permissions}
          />
        }
      />

      <div className="max-w-screen-xl p-5 pt-16 mx-auto sm:pt-24">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold text-primary">
            Modules (Slide Panel)
          </h2>
      </div>

      {/* SEARCH MODAL */}
      {showSearchModal && (
        <SearchEverythingModal
          globalSearch={globalSearch}
          setGlobalSearch={setGlobalSearch}
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          handleGlobalSearch={handleGlobalSearch}
          searchLoading={searchLoading}
          searchError={searchError}
          searchResults={searchResults}
          setSearchResults={setSearchResults}
          modulesList={modulesList}
          selectedModules={selectedModules}
          setSelectedModules={setSelectedModules}
          getDefaultFromDate={getDefaultFromDate}
          getDefaultToDate={getDefaultToDate}
          onClose={() => setShowSearchModal(false)}
        />
      )}

        <div className="relative mb-2 bg-[#f1f1f1] p-1 rounded-lg">
          <input
            ref={searchRef}
            value={mainSearch}
            onChange={(e) => setMainSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                const firstItem = document.querySelector('.menu-item');
                if (firstItem) firstItem.focus();
              }
            }}
            placeholder="Search main menus..."
            className="w-full h-10 pl-10 text-sm bg-white input input-bordered"
          />
          <Search className="absolute w-6 h-6 -translate-y-1/2 left-3 top-1/2 text-base-content/70" />
        </div>

        {/* LIST VIEW WHEN SEARCHING */}
        {mainSearch.trim() !== "" ? (
          <div className="space-y-2 min-h-[70vh]">
            {filteredItems.length === 0 && (
              <p className="text-sm text-base-content/60">No menus found</p>
            )}

            {filteredItems.map((item, i) => {
              if (item.type === 'menu') {
                const m = item.data;
                return (
                  <div
                    key={`menu-${m.originalIndex}`}
                    onClick={() => openPanelFor(m.originalIndex, m)}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        const next = e.target.nextElementSibling;
                        if (next && next.classList.contains('menu-item')) next.focus();
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        const prev = e.target.previousElementSibling;
                        if (prev && prev.classList.contains('menu-item')) prev.focus();
                      } else if (e.key === 'Enter') {
                        e.target.click();
                      }
                    }}
                    className="p-3 rounded-lg cursor-pointer border border-[rgba(var(--base-content-rgb),0.1)]
                               hover:bg-[rgba(var(--base-200-rgb),0.2)] transition menu-item"
                    tabIndex="0"
                  >
                    <div className="text-lg font-semibold text-base-content">
                      {m.MdlDisplayName || m.ModuleType}
                    </div>
                    <div className="text-xs text-base-content/60">
                      {allSubMenus[menuKey(m.ModuleType)]?.length || 0} Modules
                    </div>
                  </div>
                );
               } else {
                 const s = item.data;
                 const Icon = getMenuGroupIcon(s.Mnu_Group);
                 return (
                   <div
                     key={`submenu-${i}`}
                     onClick={() => {
                       if (s.ReactWebPath) {
                         const url = buildReactWebPathUrl(s.ReactWebPath, {
                           mdlId: s.MdlId,
                           TxnType: s.TxnType,
                           TxnSubType: s.TxnSubType,
                         });
                         window.location.href = url;
                       }
                     }}
                     onKeyDown={(e) => {
                       if (e.key === 'ArrowDown') {
                         e.preventDefault();
                         const next = e.target.nextElementSibling;
                         if (next && next.classList.contains('menu-item')) next.focus();
                       } else if (e.key === 'ArrowUp') {
                         e.preventDefault();
                         const prev = e.target.previousElementSibling;
                         if (prev && prev.classList.contains('menu-item')) prev.focus();
                       } else if (e.key === 'Enter') {
                         e.target.click();
                       }
                     }}
                     className="p-3 rounded-lg cursor-pointer border border-[rgba(var(--base-content-rgb),0.1)]
                                hover:bg-[rgba(var(--base-200-rgb),0.2)] transition menu-item"
                     tabIndex="0"
                   >
                     <div className="flex items-center gap-2 text-lg font-semibold text-base-content">
                       <Icon className="w-5 h-5" />
                       {s.MdlDisplayName}
                     </div>
                     <div className="text-xs text-base-content/60">
                       in {s.parentMenu.ModuleType}
                     </div>
                   </div>
                 );
               }
            })}
          </div>
        ) : (
          /* CARD VIEW */
          <div
            className="grid content-start gap-3"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
          >
            {filteredMenus.map((m) => {
              const Icon = icons[m.originalIndex % icons.length];
              return (
                <div
                  key={m.originalIndex}
                  onClick={() => openPanelFor(m.originalIndex, m)}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      const next = e.target.nextElementSibling;
                      if (next && next.classList.contains('menu-item')) next.focus();
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      const prev = e.target.previousElementSibling;
                      if (prev && prev.classList.contains('menu-item')) prev.focus();
                    } else if (e.key === 'Enter') {
                      e.target.click();
                    }
                  }}
                  className="w-60 h-[72px] cursor-pointer rounded-lg p-3
                             bg-gradient-to-br from-[rgba(var(--primary-rgb),0.2)]
                             via-[rgba(var(--primary-focus-rgb),0.1)]
                             to-[rgba(var(--secondary-rgb),0.1)]
                             border border-[rgba(var(--base-content-rgb),0.1)]
                             backdrop-blur-xl shadow hover:scale-105 hover:shadow-xl
                             transition-all menu-item"
                  tabIndex="0"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold truncate text-primary">
                        {m.MdlDisplayName || m.ModuleType}
                      </h3>
                      <p className="text-xs text-primary/70"> </p>
                    </div>
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* PANEL BACKDROP */}
      {panelOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50"
          onClick={closePanel}
        ></div>
      )}

      {/* SLIDE PANEL */}
      <div
        className={`fixed inset-y-0 right-0 z-50 w-full sm:w-96 transform transition-transform duration-300 
        ${panelOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="h-full p-6 border-l shadow-xl bg-white/20 backdrop-blur-xl border-white/30">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-semibold">
                {menus[activeIndex]?.MdlDisplayName || menus[activeIndex]?.ModuleType}
              </h3>
              <p className="text-xs text-base-content/70">Select module</p>
            </div>
            <button
              onClick={closePanel}
              className="text-lg font-bold text-base-content"
            >
              ✕
            </button>
          </div>

          <input
            ref={subSearchRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                document.querySelector(".sub-menu-item")?.focus();
              }
            }}
            placeholder="Search modules..."
            className="w-full mb-2 border input h-9 bg-white/40 backdrop-blur"
          />

          <div className="overflow-auto max-h-[70vh]">
            {filteredSubs.length === 0 && (
              <p className="text-sm text-base-content/60">No modules found</p>
            )}

                {filteredSubs.map((s, i) => {
                  const Icon = getMenuGroupIcon(s.Mnu_Group);
                  return (
                    <div
                      key={i}
                      onClick={() => {
                        if (s.ReactWebPath) {
                          const url = buildReactWebPathUrl(s.ReactWebPath, {
                            mdlId: s.MdlId,
                            TxnType: s.TxnType,
                            TxnSubType: s.TxnSubType,
                          });
                          window.location.href = url;
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "ArrowDown") {
                          e.preventDefault();
                          const next = e.currentTarget.nextElementSibling;
                          if (next?.classList.contains("sub-menu-item")) next.focus();
                        } else if (e.key === "ArrowUp") {
                          e.preventDefault();
                          const prev = e.currentTarget.previousElementSibling;
                          if (prev?.classList.contains("sub-menu-item")) {
                            prev.focus();
                          } else {
                            subSearchRef.current?.focus();
                          }
                        } else if (e.key === "Enter") {
                          e.currentTarget.click();
                        }
                      }}
                      className="p-3 mb-1 transition rounded-lg cursor-pointer hover:bg-base-200/50 focus:outline-none focus:bg-base-200/50 sub-menu-item"
                      tabIndex="0"
                    >
                      <div className="flex items-center gap-2 text-lg font-bold">
                        <Icon className="w-5 h-5" />
                        {s.MdlDisplayName}
                      </div>
                    </div>
                  );
                })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   C) BOTTOM DRAWER VARIANT
============================================================ */
export function MenuBottomDrawerVariant() {
  const { menus, allSubMenus } = useMenuData();
  const permissions = useConfigPermissions();
  const uid = sessionStorage.getItem("userId");
  const navigate = useNavigate();

  // Map Mnu_Group to corresponding icon
  const getMenuGroupIcon = (menuGroup) => {
    switch (menuGroup?.trim()) {
      case "Entry Form":
        return FileInput;
      case "Report Form":
        return FileTextIcon;
      case "Misc Form":
        return FileSpreadsheet;
      default:
        return FileTextIcon; // Default icon
    }
  };

  // Helper function to get default date range (last 30 days)
  const getDefaultFromDate = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    return thirtyDaysAgo.toISOString().split('T')[0];
  };

  const getDefaultToDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const [activeIndex, setActiveIndex] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [subList, setSubList] = useState([]);
  const [search, setSearch] = useState("");
  const [mainSearch, setMainSearch] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getDefaultToDate());
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [modulesList, setModulesList] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const searchRef = useRef(null);
  const subSearchRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") closeDrawer();
      if (e.key === "F9") {
        e.preventDefault();
        navigate("/advance-report");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate]);

  useEffect(() => {
    setTimeout(() => {
      if (searchRef.current) {
        searchRef.current.focus();
      }
    }, 100);
  }, []);

  useEffect(() => {
    const loadModules = async () => {
      try {
        const uidLocal = sessionStorage.getItem("userId") || localStorage.getItem("userId");
        const resp = await api.GetAllSubMenuList(uidLocal);
        const list = resp?.Table || [];
        setModulesList(list);
      } catch (err) {
        setModulesList([]);
      }
    };
    loadModules();
  }, []);

  const handleGlobalSearch = async () => {
    setSearchError("");
    setSearchLoading(true);
    
    // Validate that dates are provided
    if (!fromDate || !toDate) {
      setSearchError("Date range is mandatory");
      setSearchLoading(false);
      return;
    }

    try {
      const user = sessionStorage.getItem("userName") || localStorage.getItem("userName") || "";
      const useDateFilter = 1; // Dates are always mandatory
      const mdlIds = selectedModules.length > 0 ? (selectedModules || []).join(",") : null;
      const resp = await api.GetSearchAndLog(globalSearch || "", user, useDateFilter, mdlIds, fromDate, toDate);

      // For each log row, fetch module display values
      const rows = resp?.logData?.Table || [];
      const moduleDetails = await Promise.all(rows.map(async (r) => {
        try {
          const md = await api.GetModuleDisplayValues(r.MdlId, r.PrimeId);
          return { log: r, moduleData: md?.data || md };
        } catch (e) {
          return { log: r, error: e?.message || String(e) };
        }
      }));

      setSearchResults({ ...resp, moduleDetails });
    } catch (err) {
      setSearchError(err?.message || "Failed to fetch");
      setSearchResults(null);
    } finally {
      setSearchLoading(false);
    }
  };

  const openDrawerFor = async (idx, menu) => {
    setActiveIndex(idx);
    setSearch("");
    setDrawerOpen(true);

    try {
      const resp = await api.GetSubMenuList(uid, menu.ModuleType);
      setSubList(resp.Table || []);
    } catch {
      setSubList([]);
    }

    setTimeout(() => {
      subSearchRef.current?.focus();
    }, 60);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setActiveIndex(null);
  };

  const filteredItems = [];
  const filteredMenus = [];
  if (mainSearch.trim()) {
    const q = mainSearch.toLowerCase();
    menus.forEach((m, idx) => {
      const menuMatches = (m.MdlDisplayName || m.ModuleType)?.toLowerCase().includes(q);
      if (menuMatches) {
        const menuItem = { type: 'menu', data: { ...m, originalIndex: idx } };
        filteredItems.push(menuItem);
        filteredMenus.push(menuItem.data);
      }
      const matchingSubs = allSubMenus[menuKey(m.ModuleType)]?.filter((s) =>
        s.MdlDisplayName?.toLowerCase().includes(q)
      ) || [];
      matchingSubs.forEach((s) => {
        filteredItems.push({ type: 'submenu', data: { ...s, parentMenu: m, parentIndex: idx } });
      });
    });
  } else {
    const menuItems = menus.map((m, idx) => ({ type: 'menu', data: { ...m, originalIndex: idx } }));
    filteredItems.push(...menuItems);
    filteredMenus.push(...menuItems.map(item => item.data));
  }

  const filteredSubs = subList.filter((s) =>
    s.MdlDisplayName?.toLowerCase().includes(search.toLowerCase())
  );

  const icons = [Folder, Settings, Users, BarChart3, Database, FileText];

  return (
    <div className="min-h-screen">
      <Header
        headerActions={
          <HeaderActionButtons
            onSearch={() => setShowSearchModal(true)}
            onReports={() => navigate("/advance-report")}
            onDashboard={() => navigate("/dashboard")}
            permissions={permissions}
          />
        }
      />

      {/* MAIN MENU */}
      <div className="max-w-screen-xl p-5 pt-16 mx-auto sm:pt-24">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold text-primary">
            Modules (Bottom Drawer)
          </h2>
    </div>

      {/* SEARCH MODAL */}
      {showSearchModal && (
        <SearchEverythingModal
          globalSearch={globalSearch}
          setGlobalSearch={setGlobalSearch}
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          handleGlobalSearch={handleGlobalSearch}
          searchLoading={searchLoading}
          searchError={searchError}
          searchResults={searchResults}
          setSearchResults={setSearchResults}
          modulesList={modulesList}
          selectedModules={selectedModules}
          setSelectedModules={setSelectedModules}
          getDefaultFromDate={getDefaultFromDate}
          getDefaultToDate={getDefaultToDate}
          onClose={() => setShowSearchModal(false)}
        />
      )}

        <div className="relative mb-2 bg-[#f1f1f1] p-1 rounded-lg">
          <input
            ref={searchRef}
            value={mainSearch}
            onChange={(e) => setMainSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                const firstItem = document.querySelector('.menu-item');
                if (firstItem) firstItem.focus();
              }
            }}
            placeholder="Search main menus..."
            className="w-full h-10 pl-10 text-sm bg-white input input-bordered"
          />
          <Search className="absolute w-6 h-6 -translate-y-1/2 left-3 top-1/2 text-base-content/70" />
        </div>

        {/* LIST VIEW WHEN SEARCHING */}
        {mainSearch.trim() !== "" ? (
          <div className="space-y-2 min-h-[70vh]">
            {filteredItems.length === 0 && (
              <p className="text-sm text-base-content/60">No menus found</p>
            )}

            {filteredItems.map((item, i) => {
              if (item.type === 'menu') {
                const m = item.data;
                return (
                  <div
                    key={`menu-${m.originalIndex}`}
                    onClick={() => openDrawerFor(m.originalIndex, m)}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        const next = e.target.nextElementSibling;
                        if (next && next.classList.contains('menu-item')) next.focus();
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        const prev = e.target.previousElementSibling;
                        if (prev && prev.classList.contains('menu-item')) prev.focus();
                      } else if (e.key === 'Enter') {
                        e.target.click();
                      }
                    }}
                    className="p-3 rounded-lg cursor-pointer border border-[rgba(var(--base-content-rgb),0.1)]
                               hover:bg-[rgba(var(--base-200-rgb),0.2)] transition menu-item"
                    tabIndex="0"
                  >
                    <div className="text-lg font-semibold text-base-content">
                      {m.MdlDisplayName || m.ModuleType}
                    </div>
                    <div className="text-xs text-base-content/60">
                      {allSubMenus[menuKey(m.ModuleType)]?.length || 0} Modules
                    </div>
                  </div>
                );
               } else {
                 const s = item.data;
                 const Icon = getMenuGroupIcon(s.Mnu_Group);
                 return (
                   <div
                     key={`submenu-${i}`}
                     onClick={() => {
                       if (s.ReactWebPath) {
                         const url = buildReactWebPathUrl(s.ReactWebPath, {
                           mdlId: s.MdlId,
                           TxnType: s.TxnType,
                           TxnSubType: s.TxnSubType,
                         });
                         window.location.href = url;
                       }
                     }}
                     onKeyDown={(e) => {
                       if (e.key === 'ArrowDown') {
                         e.preventDefault();
                         const next = e.target.nextElementSibling;
                         if (next && next.classList.contains('menu-item')) next.focus();
                       } else if (e.key === 'ArrowUp') {
                         e.preventDefault();
                         const prev = e.target.previousElementSibling;
                         if (prev && prev.classList.contains('menu-item')) prev.focus();
                       } else if (e.key === 'Enter') {
                         e.target.click();
                       }
                     }}
                     className="p-3 rounded-lg cursor-pointer border border-[rgba(var(--base-content-rgb),0.1)]
                                hover:bg-[rgba(var(--base-200-rgb),0.2)] transition menu-item"
                     tabIndex="0"
                   >
                     <div className="flex items-center gap-2 text-lg font-semibold text-base-content">
                       <Icon className="w-5 h-5" />
                       {s.MdlDisplayName}
                     </div>
                     <div className="text-xs text-base-content/60">
                       in {s.parentMenu.ModuleType}
                     </div>
                   </div>
                 );
               }
            })}
          </div>
        ) : (
          /* CARD VIEW */
          <div
            className="grid content-start gap-3"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
          >
            {filteredMenus.map((m) => {
              const Icon = icons[m.originalIndex % icons.length];
              return (
                <div
                  key={m.originalIndex}
                  onClick={() => openDrawerFor(m.originalIndex, m)}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      const next = e.target.nextElementSibling;
                      if (next && next.classList.contains('menu-item')) next.focus();
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      const prev = e.target.previousElementSibling;
                      if (prev && prev.classList.contains('menu-item')) prev.focus();
                    } else if (e.key === 'Enter') {
                      e.target.click();
                    }
                  }}
                  className="w-60 h-[72px] cursor-pointer rounded-lg p-3
                             bg-gradient-to-br from-[rgba(var(--primary-rgb),0.2)]
                             via-[rgba(var(--primary-focus-rgb),0.1)]
                             to-[rgba(var(--secondary-rgb),0.1)]
                             border border-[rgba(var(--base-content-rgb),0.1)]
                             backdrop-blur-xl shadow hover:scale-105 hover:shadow-xl
                             transition-all menu-item"
                  tabIndex="0"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold truncate text-primary">
                        {m.MdlDisplayName || m.ModuleType}
                      </h3>
                      <p className="text-xs text-primary/70"> </p>
                    </div>
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* BACKDROP */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50"
          onClick={closeDrawer}
        ></div>
      )}

      {/* DRAWER */}
      <div
        className={`fixed left-0 right-0 bottom-0 z-50 transition-transform duration-300 
        ${drawerOpen ? "translate-y-0" : "translate-y-full"}`}
      >
        <div className="bg-white/20 backdrop-blur-xl border-t border-white/30 rounded-t-2xl p-6 max-h-[70vh] overflow-auto shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-semibold">
                {menus[activeIndex]?.MdlDisplayName || menus[activeIndex]?.ModuleType}
              </h3>
              <p className="text-xs text-base-content/70">Tap a module</p>
            </div>
            <button
              onClick={closeDrawer}
              className="text-lg font-bold text-base-content"
            >
              ✕
            </button>
          </div>

          <input
            ref={subSearchRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                document.querySelector(".sub-menu-item")?.focus();
              }
            }}
            placeholder="Search modules..."
            className="w-full mb-2 border input h-9 bg-white/40 backdrop-blur"
          />

          <div className="overflow-auto max-h-[70vh]">
            {filteredSubs.length === 0 && (
              <p className="p-3 text-sm text-base-content/60">No modules found</p>
            )}

                {filteredSubs.map((s, i) => {
                  const Icon = getMenuGroupIcon(s.Mnu_Group);
                  return (
                    <div
                      key={i}
                      onClick={() => {
                        if (s.ReactWebPath) {
                          const url = buildReactWebPathUrl(s.ReactWebPath, {
                            mdlId: s.MdlId,
                            TxnType: s.TxnType,
                            TxnSubType: s.TxnSubType,
                          });
                          window.location.href = url;
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "ArrowDown") {
                          e.preventDefault();
                          const next = e.currentTarget.nextElementSibling;
                          if (next?.classList.contains("sub-menu-item")) next.focus();
                        } else if (e.key === "ArrowUp") {
                          e.preventDefault();
                          const prev = e.currentTarget.previousElementSibling;
                          if (prev?.classList.contains("sub-menu-item")) {
                            prev.focus();
                          } else {
                            subSearchRef.current?.focus();
                          }
                        } else if (e.key === "Enter") {
                          e.currentTarget.click();
                        }
                      }}
                      className="p-3 mb-1 transition rounded-lg cursor-pointer hover:bg-base-200/50 focus:outline-none focus:bg-base-200/50 sub-menu-item"
                      tabIndex="0"
                    >
                      <div className="flex items-center gap-2 text-lg font-bold">
                        <Icon className="w-5 h-5" />
                        {s.MdlDisplayName}
                      </div>
                    </div>
                  );
                })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   VARIANT SWITCHER
============================================================ */
export default function MenuPageVariants() {
  const [variant, setVariant] = useState("A");

  const cycleVariant = () => {
    if (variant === "A") setVariant("B");
    else if (variant === "B") setVariant("C");
    else setVariant("A");
  };

  return (
    <div>
      <button
        onClick={cycleVariant}
        className="fixed z-50 shadow-lg bottom-4 right-4 btn btn-circle btn-primary"
      >
        🔄
      </button>

      {variant === "A" && <MenuModalVariant />}
      {variant === "B" && <MenuSlidePanelVariant />}
      {variant === "C" && <MenuBottomDrawerVariant />}
    </div>
  );
}
